import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  AutocompleteAsyncField,
  InputField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

const client = "instit-quantilia";
const table_name = "input_files";

const FormSchema = z.object({
  protocol: z.string().nullable().optional(),
  connector: z.string().nullable().optional(),
  path: z.string().nullable().optional(),
  filename: z.string().nullable().optional(),
  connector_config: z
    .union([
      z.string().nullable().optional(),
      z.object({
        id: z.number(),
        text: z.string().nullable().optional(),
      }),
    ])
    .nullable()
    .optional(),
  connector_config_id: z.coerce.number().nullable().optional(),
  unique_attribute_id: z.string().nullable().optional(),
});

export function InputFilesNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Input File
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="protocol"
                    label={t(`${table_name}.columns.protocol`, {
                      ns: "tables",
                      defaultValue: "Protocol",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="connector"
                    label={t(`${table_name}.columns.connector`, {
                      ns: "tables",
                      defaultValue: "Connector",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="connector_config"
                    label="Connector Config"
                    base_url="etl-quantilia/connector_configs"
                    column_name="path"
                    id_column="connector_config_id"
                    control={form.control}
                  />
                  <InputField
                    name="path"
                    label={t(`${table_name}.columns.path`, {
                      ns: "tables",
                      defaultValue: "Path",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="filename"
                    label={t(`${table_name}.columns.filename`, {
                      ns: "tables",
                      defaultValue: "Filename",
                    })}
                    control={form.control}
                  />
                </div>
                {/* Row 3 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="unique_attribute_id"
                    label={t(`${table_name}.columns.unique_attribute_id`, {
                      ns: "tables",
                      defaultValue: "Unique Attribute ID",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? NewDiff(changedFields, ["connector_config"])
              : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
